import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useState } from 'react';
import { useInvalidateBanking } from '@/components/banking/line-editor';
import { RuleDrawer } from '@/components/banking/rule-drawer';
import { ErrorBanner } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useBankAccounts, useBankRules, type BankRule } from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { i18n, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useAccountNames } from '@/lib/accounts';

export const Route = createFileRoute('/banking/rules')({ component: RulesPage });

const P = 'purchasePages';

/** "Money in" / "Money out" / "Money in or out" in the user's language. */
const appliesLabel = (a: BankRule['appliesTo']): string =>
  i18n.t(
    a === 'money_in'
      ? `${P}.bankLabels.moneyIn`
      : a === 'money_out'
        ? `${P}.bankLabels.moneyOut`
        : `${P}.bankLabels.moneyInOrOut`,
  );

function describeConditions(r: Pick<BankRule, 'conditions' | 'matchAll'>): string {
  return r.conditions
    .map((c) =>
      c.field === 'amount'
        ? i18n.t(`${P}.rules.condAmount.${c.op}`, { value: formatAmount(c.value) })
        : i18n.t(`${P}.rules.condText.${c.op}`, { value: c.value }),
    )
    .join(i18n.t(r.matchAll ? `${P}.rules.joinAnd` : `${P}.rules.joinOr`));
}

/** Screen B3 (document 06): bank rules in priority order. */
function RulesPage() {
  const t = useT();
  const names = useAccountNames();
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const rules = useBankRules();
  const banks = useBankAccounts();
  const invalidate = useInvalidateBanking();
  const [drawer, setDrawer] = useState<{ open: boolean; rule: BankRule | null }>({
    open: false,
    rule: null,
  });
  const [deleting, setDeleting] = useState<BankRule | null>(null);

  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      api<BankRule[]>(`/orgs/${orgId}/banking/rules/order`, {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => invalidate(),
  });
  const remove = useMutation({
    mutationFn: (r: BankRule) => api(`/orgs/${orgId}/banking/rules/${r.id}`, { method: 'DELETE' }),
    onSuccess: async (_x, r) => {
      await invalidate();
      toast(t('purchasePages.rules.deleted', { name: r.name }));
      setDeleting(null);
    },
  });
  const move = (i: number, by: -1 | 1) => {
    const ids = (rules.data ?? []).map((r) => r.id);
    const j = i + by;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };

  return (
    <>
      <PageHeader
        title={t('purchasePages.rules.title')}
        description={t('purchasePages.rules.description')}
        actions={
          canEdit ? (
            <Button onClick={() => setDrawer({ open: true, rule: null })}>
              {t('purchasePages.rules.newRule')}
            </Button>
          ) : null
        }
      />
      <ErrorBanner>{reorder.error?.message ?? null}</ErrorBanner>
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} />
      ) : rules.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : rules.isError ? (
        <EmptyState title={t('purchasePages.rules.loadFailed')} hint={rules.error.message} />
      ) : rules.data.length === 0 ? (
        <EmptyState
          title={t('purchasePages.rules.noRules')}
          hint={t('purchasePages.rules.noRulesHint')}
          action={
            canEdit ? (
              <Button onClick={() => setDrawer({ open: true, rule: null })}>
                {t('purchasePages.rules.newRule')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[90px]">{t('purchasePages.rules.col.priority')}</TableHead>
                <TableHead>{t('purchasePages.rules.col.ruleName')}</TableHead>
                <TableHead>{t('purchasePages.rules.col.appliedTo')}</TableHead>
                <TableHead>{t('purchasePages.rules.col.conditions')}</TableHead>
                <TableHead>{t('purchasePages.rules.col.settings')}</TableHead>
                <TableHead className="w-[110px] text-end">
                  {t('purchasePages.rules.col.inForReview')}
                </TableHead>
                <TableHead className="w-[150px] text-end">
                  {t('purchasePages.rules.col.action')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.data.map((r, i) => (
                <TableRow key={r.id} data-testid="rule-row">
                  <TableCell>
                    <span className="inline-flex items-center gap-1">
                      <span className="tnum w-5 text-end">{r.priority}</span>
                      {canEdit ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label={t('purchasePages.rules.moveUp', { name: r.name })}
                            disabled={i === 0 || reorder.isPending}
                            onClick={() => move(i, -1)}
                          >
                            <ArrowUp />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label={t('purchasePages.rules.moveDown', { name: r.name })}
                            disabled={i === rules.data.length - 1 || reorder.isPending}
                            onClick={() => move(i, 1)}
                          >
                            <ArrowDown />
                          </Button>
                        </>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.name}
                    {r.autoAdd ? (
                      <Badge variant="secondary" className="ms-2">
                        {t('purchasePages.rules.autoAdd')}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div>{appliesLabel(r.appliesTo)}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.bankAccountName
                        ? names.codeName(r.bankAccountId, r.bankAccountName)
                        : t('purchasePages.bankLabels.allBankAccounts')}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[320px] whitespace-normal text-sm">
                    {describeConditions(r)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.action === 'exclude' ? (
                      t('purchasePages.bankLabels.exclude')
                    ) : (
                      <>
                        <div>{r.accountName && names.codeName(r.accountId, r.accountName)}</div>
                        <div className="text-xs text-muted-foreground">
                          {[
                            r.taxCode
                              ? t('purchasePages.rules.vatCode', { code: r.taxCode })
                              : null,
                            r.contactName,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="tnum text-end">{r.suggestedCount}</TableCell>
                  <TableCell className="text-end">
                    {canEdit ? (
                      <>
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2"
                          onClick={() => setDrawer({ open: true, rule: r })}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2 text-destructive"
                          onClick={() => setDeleting(r)}
                        >
                          {t('purchasePages.bankLabels.delete')}
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RuleDrawer
        open={drawer.open}
        rule={drawer.rule}
        banks={banks.data ?? []}
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
      <Dialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('purchasePages.rules.deleteTitle', { name: deleting?.name ?? '' })}
            </DialogTitle>
            <DialogDescription>{t('purchasePages.rules.deleteBody')}</DialogDescription>
          </DialogHeader>
          {remove.error ? (
            <p role="alert" className="text-sm text-destructive">
              {remove.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {t('purchasePages.bankLabels.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
